import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { plPL } from '@mui/material/locale';

type ThemeMode = 'light' | 'dark';

interface ThemeContextType {
  mode: ThemeMode;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType>({
  mode: 'light',
  toggleTheme: () => {},
});

export const useTheme = () => useContext(ThemeContext);

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Sprawdź zapisany motyw w localStorage lub użyj domyślnego jasnego motywu
  const [mode, setMode] = useState<ThemeMode>(() => {
    const savedMode = localStorage.getItem('themeMode');
    return (savedMode as ThemeMode) || 'light';
  });

  // Tworzenie motywu w zależności od wybranego trybu
  const theme = React.useMemo(
    () =>
      createTheme(
        {
          palette: {
            mode,
            primary: {
              main: '#1976d2',
              light: '#42a5f5',
              dark: '#1565c0',
            },
            secondary: {
              main: '#9c27b0',
              light: '#ba68c8',
              dark: '#7b1fa2',
            },
            error: {
              main: '#d32f2f',
              light: '#ef5350',
              dark: '#c62828',
            },
            warning: {
              main: '#ed6c02',
              light: '#ff9800',
              dark: '#e65100',
            },
            info: {
              main: '#0288d1',
              light: '#03a9f4',
              dark: '#01579b',
            },
            success: {
              main: '#2e7d32',
              light: '#4caf50',
              dark: '#1b5e20',
            },
            background: {
              default: mode === 'light' ? '#f5f5f5' : '#121212',
              paper: mode === 'light' ? '#ffffff' : '#1e1e1e',
            },
          },
          typography: {
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
            h1: {
              fontWeight: 500,
            },
            h2: {
              fontWeight: 500,
            },
            h3: {
              fontWeight: 500,
            },
            h4: {
              fontWeight: 500,
            },
            h5: {
              fontWeight: 500,
            },
            h6: {
              fontWeight: 500,
            },
          },
          shape: {
            borderRadius: 8,
          },
          components: {
            MuiButton: {
              styleOverrides: {
                root: {
                  textTransform: 'none',
                  fontWeight: 500,
                },
              },
            },
            MuiCard: {
              styleOverrides: {
                root: {
                  borderRadius: 8,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                },
              },
            },
            MuiCardHeader: {
              styleOverrides: {
                root: {
                  padding: '16px 24px',
                },
              },
            },
            MuiCardContent: {
              styleOverrides: {
                root: {
                  padding: '24px',
                },
              },
            },
            MuiPaper: {
              styleOverrides: {
                root: {
                  borderRadius: 8,
                },
              },
            },
          },
        },
        plPL, // Dodajemy polskie tłumaczenia dla komponentów
      ),
    [mode]
  );

  // Przełączanie motywu
  const toggleTheme = () => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  };

  // Zapisz motyw w localStorage przy każdej zmianie
  useEffect(() => {
    localStorage.setItem('themeMode', mode);
  }, [mode]);

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme }}>
      <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
